import { memo, useState } from 'react';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import { contactsLabelsSelectOneObjectByQuery, labelSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { selectUserObject } from '@common-reducers/UserSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Snooze } from '@common-reducers/labels-reducer';
import { toggleChatLabelThunk } from '@common-reducers/labels-reducer/labels-thunks';
import { Moon, Loader2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from "@common-config/constants";

type TargetHandleLater = '1 hour' | '4 hours' | 'tomorrow' | 'next week'
type Props = {
    waChatId: string
    size?: number
}

// Separate component for active state to handle hover
const HandleLaterActiveButton = memo(function HandleLaterActiveButton({
    size = 16,
    isLoading,
    onClick,
    doneLabel,
    removeHandleLaterLabel
}: {
    size?: number;
    isLoading: boolean;
    onClick: () => void;
    doneLabel: string;
    removeHandleLaterLabel: string;
}) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        className="p-1 rounded transition-colors cursor-pointer text-purple-500 hover:text-green-500 outline-none"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClick();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {isLoading ? (
                            <Loader2 size={size} className="animate-spin text-gray-400" />
                        ) : isHovered ? (
                            <Check size={size} className="currentColor" strokeWidth={2} />
                        ) : (
                            <Moon size={size} className="currentColor" strokeWidth={2} />
                        )}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>{isHovered ? doneLabel : removeHandleLaterLabel}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
})

export default memo(function HandleLaterPopper({ waChatId, size = 16 }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const authUser = useAppSelector(selectUserObject);
    const loadingState = useAppSelector((state) => state.LabelReducer.loadingState || []) // safeguard
    const portalContainer = usePortalContainer();

    const label = useAppSelector(state => labelSelectOneObjectByQuery(state.DBLabelReducer, {
        name: 'handle later'
    }))

    const contactLabel = useAppSelector((state) => contactsLabelsSelectOneObjectByQuery(state.DBContactsLabelsReducer, {
        labelId: label?._id,
        waChatId
    }))

    // Check if handle later is active for this chat
    const isHandleLaterActive = authUser?._id && contactLabel?.labelId === label?._id;
    const isLoading = loadingState.includes(`${waChatId}-${label?.name} `);

    const currentDate = new Date(new Date().toISOString())
    function getTime(hourIncrement?: number, target?: TargetHandleLater) {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        if (target === 'next week') {
            const _currentDate = new Date(new Date().toISOString())
            const futureNextWeek = _currentDate.setDate(new Date().getDate() + 7)
            const nextWeek = new Date(futureNextWeek)
            return `${days[nextWeek.getDay()]}, ${nextWeek.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} `

        } else if (target === 'tomorrow') {
            // Tomorrow same time
            const futureDate = new Date(currentDate.getTime() + 60 * 24 * 60 * 1000)
            return `${days[futureDate.getDay()]}, ${futureDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} `
        }
        else {
            const futureDate = new Date(currentDate.getTime() + 60 * hourIncrement! * 60 * 1000)
            return `${futureDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} `
        }
    }


    function handleHandleLater(targetHandleLater: TargetHandleLater) {
        const handleLater: Snooze = {}
        if (targetHandleLater === '1 hour') {
            const futureDate = new Date(currentDate.getTime() + 60 * 1 * 60 * 1000)
            handleLater.expiryDate = futureDate.toISOString()
        } else if (targetHandleLater === '4 hours') {
            const futureDate = new Date(currentDate.getTime() + 60 * 4 * 60 * 1000)
            handleLater.expiryDate = futureDate.toISOString()
        } else if (targetHandleLater === 'next week') {
            // next week same time
            const _currentDate = new Date(new Date().toISOString())
            const futureNextWeek = _currentDate.setDate(new Date().getDate() + 7)
            handleLater.expiryDate = new Date(futureNextWeek).toISOString()
        } else if (targetHandleLater === 'tomorrow') {
            // tomorrow same time
            const futureTomorrow = new Date(currentDate.getTime() + 60 * 24 * 60 * 1000)
            handleLater.expiryDate = futureTomorrow.toISOString()
        }
        dispatch(toggleChatLabelThunk({
            labelId: label?._id ?? '',
            waChatId,
            snooze: handleLater
        }))
    }

    // If not logged in, show login prompt
    if (!authUser?._id) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            className="p-1 rounded transition-colors cursor-pointer hover:text-gray-600 text-gray-500 outline-none"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch(accessFormPopupActions.setStatus('Login'));
                            }}
                        >
                            <Moon size={size} className="currentColor" strokeWidth={2} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('labels.handleLater')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    // If already active, just click to remove (no menu)
    if (isHandleLaterActive) {
        return (
            <HandleLaterActiveButton
                size={size}
                isLoading={isLoading}
                onClick={() => {
                    dispatch(toggleChatLabelThunk({
                        labelId: label?._id ?? '',
                        waChatId,
                    }));
                }}
                doneLabel={t('labels.done')}
                removeHandleLaterLabel={t('labels.removeHandleLater')}
            />
        );
    }

    // Not active - show dropdown menu to select time
    return (
        <DropdownMenu>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="p-1 rounded transition-colors cursor-pointer outline-none text-gray-500 hover:text-gray-600"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    {isLoading ? (
                                        <Loader2 size={size} className="animate-spin text-gray-400" />
                                    ) : (
                                        <Moon size={size} className="currentColor" strokeWidth={2} />
                                    )}
                                </button>
                            </DropdownMenuTrigger>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('labels.handleLater')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <DropdownMenuContent
                align="end"
                container={portalContainer}
                className="w-48"
            >
                <DropdownMenuItem
                    onClick={() => handleHandleLater('1 hour')}
                    className="flex justify-between w-full cursor-pointer"
                >
                    <span>{t('labels.oneHour')}</span>
                    <span className="text-xs text-gray-400">{getTime(1)}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleHandleLater('4 hours')}
                    className="flex justify-between w-full cursor-pointer"
                >
                    <span>{t('labels.fourHours')}</span>
                    <span className="text-xs text-gray-400">{getTime(4)}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleHandleLater('tomorrow')}
                    className="flex justify-between w-full cursor-pointer"
                >
                    <span>{t('labels.tomorrow')}</span>
                    <span className="text-xs text-gray-400">{getTime(0)}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleHandleLater('next week')}
                    className="flex justify-between w-full cursor-pointer"
                >
                    <span>{t('labels.nextWeek')}</span>
                    <span className="text-xs text-gray-400">{getTime(0, 'next week')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
})